import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_BASE_URL from '../config/api';

const ResetPasswordPage = () => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useTheme();

  const logoSrc = isDarkMode
    ? 'https://safaviehimagelinks.s3.us-east-1.amazonaws.com/ChatGPT+Image+Aug+5%2C+2025%2C+12_21_33+PM.png'
    : 'https://safaviehimagelinks.s3.us-east-1.amazonaws.com/Screenshot+2025-05-10+230511.png';

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setMessage('Invalid or missing token.');
    }
  }, [location]);

  const validatePassword = (pw) => {
    if (!pw || pw.length < 8) return 'Password must be at least 8 characters long.';
    if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter.';
    if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter.';
    if (!/[0-9]/.test(pw)) return 'Password must contain at least one number.';
    return null;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');

    const pwErr = validatePassword(newPassword);
    if (pwErr) {
      setMessage(pwErr);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/ResetPassword/reset-password`, {
        Token: token,
        NewPassword: newPassword,
        ConfirmPassword: confirmPassword,
      });
      setMessage(response.data.message);
      if (response.data.success) {
        setTimeout(() => navigate('/'), 2000); // Redirect to login after 2 seconds
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);
  const handleToggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <Container
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          maxWidth: '400px',
          p: 3,
          backgroundColor: 'background.default',
          borderRadius: 2,
        }}
      >
        <Box sx={{ mb: 3, width: 120, height: 120 }}>
          <img
            src={logoSrc}
            alt="Logo"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </Box>

        <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
          Reset Password
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Enter your new password below.
        </Typography>

        {message && (
          <Alert
            severity={message.includes('success') ? 'success' : 'error'}
            sx={{ width: '100%', mb: 2, bgcolor: message.includes('success') ? 'success.main' : 'error.main', color: 'background.paper' }}
          >
            {message}
          </Alert>
        )}

        {token ? (
          <form onSubmit={handleResetPassword} style={{ width: '100%' }}>
            <TextField
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderColor: 'secondary.main' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
              }}
              InputProps={{
                style: { color: 'text.primary' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleNewPasswordVisibility} edge="end">
                      {showNewPassword ? (
                        <VisibilityOff sx={{ color: 'primary.main' }} />
                      ) : (
                        <Visibility sx={{ color: 'primary.main' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'background.paper',
                  '& fieldset': { borderColor: 'secondary.main' },
                  '&:hover fieldset': { borderColor: 'primary.main' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& .MuiInputLabel-root': { color: 'text.secondary' },
                '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
              }}
              InputProps={{
                style: { color: 'text.primary' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleToggleConfirmPasswordVisibility} edge="end">
                      {showConfirmPassword ? (
                        <VisibilityOff sx={{ color: 'primary.main' }} />
                      ) : (
                        <Visibility sx={{ color: 'primary.main' }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: '8px',
                backgroundColor: 'primary.main',
                color: 'background.paper',
                '&:hover': { backgroundColor: 'primary.dark' },
                '&:disabled': { backgroundColor: 'primary.main', opacity: 0.5, color: 'background.paper' },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'background.paper' }} /> : 'Reset Password'}
            </Button>
          </form>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            Please use a valid reset link.
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;